import { fetchRadioNow } from 'components/shared/MediaDock/mediaDockRadio'

export const RADIO_NOW_FEED_INTERVAL_MS = 30 * 1000

type Payload = Awaited<ReturnType<typeof fetchRadioNow>> & { receivedAt?: number }
type Listener = (payload: Payload | null) => void
type ReadOptions = { listening?: boolean; fresh?: boolean; contenders?: boolean }
type SubscribeOptions = { listening?: boolean; contenders?: boolean }
type Deps = {
    fetchNow: (options: ReadOptions) => Promise<any>
    subscribeUpdates?: (refresh: () => void) => () => void
    intervalMs?: number
    setTimer?: (fn: () => void, ms: number) => unknown
    clearTimer?: (id: unknown) => void
    isVisible?: () => boolean
    now?: () => number
    onVisible?: (fn: () => void) => () => void
}

// One broadcast, socket subscription and clock per tab. The dock, Radio
// stream and inline cards consume the same snapshots. Only listener subscribers
// count as an audience; passive cards retain their informational reads.
export function createRadioNowFeed(deps: Deps) {
    const intervalMs = deps.intervalMs ?? RADIO_NOW_FEED_INTERVAL_MS
    const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
    const clearTimer = deps.clearTimer ?? (id => clearTimeout(id as any))
    const isVisible =
        deps.isVisible ??
        (() => typeof document === 'undefined' || document.visibilityState !== 'hidden')
    const now = deps.now ?? (() => Date.now())
    const onVisible =
        deps.onVisible ??
        (fn => {
            if (typeof document === 'undefined') return () => {}
            const handler = () => {
                if (document.visibilityState !== 'hidden') fn()
            }
            document.addEventListener('visibilitychange', handler)
            window.addEventListener('online', handler)
            return () => {
                document.removeEventListener('visibilitychange', handler)
                window.removeEventListener('online', handler)
            }
        })
    // Each subscriber says whether it is listening (an audience) and whether it
    // reads the odds chart. Cards that only show the song on air pass
    // contenders: false, and while nobody needs the chart the feed asks the
    // station without it (a full draw over the pool on the server).
    const listeners = new Map<Listener, { listening: boolean; contenders: boolean }>()
    let latest: Payload | null = null
    let timer: unknown = null
    let inFlight: Promise<any> | null = null
    let queuedRead: Promise<any> | null = null
    let inFlightListening = false
    let inFlightContenders = false
    let generation = 0
    let stopVisibility: (() => void) | undefined
    let stopUpdates: (() => void) | undefined
    const listening = () => [...listeners.values()].some(entry => entry.listening)
    const wantsContenders = () => [...listeners.values()].some(entry => entry.contenders)
    // The song on air is still playing: a snapshot kept after the last
    // subscriber left can show it at once on the next visit.
    const stillOnAir = (payload: Payload | null) => {
        if (!payload?.now) return false
        const remaining =
            (payload.now.duration - payload.now.position) * 1000 -
            (now() - (payload.receivedAt ?? now()))
        return Number.isFinite(remaining) && remaining > 0
    }

    const schedule = (failed = false) => {
        if (timer != null) clearTimer(timer)
        const remaining =
            !failed && latest?.now
                ? (latest.now.duration - latest.now.position) * 1000 -
                  (now() - (latest.receivedAt ?? now()))
                : NaN
        const delay = Number.isFinite(remaining)
            ? Math.min(intervalMs, Math.max(1000, remaining + 250))
            : intervalMs
        timer = listeners.size
            ? setTimer(() => {
                  void read()
              }, delay)
            : null
    }

    const publish = (payload: Payload | null) => {
        // A finished report and an informational read can race. Never restore
        // an older slot or an older sample of a shortened slot.
        if (
            payload &&
            latest &&
            ((payload.now && latest.now && payload.now.playIndex < latest.now.playIndex) ||
                (payload.serverNow != null &&
                    latest.serverNow != null &&
                    payload.serverNow < latest.serverNow))
        )
            return latest
        // A read without the chart keeps the chart already held for the same play.
        if (
            payload &&
            (payload as any).contendersIncluded === false &&
            latest?.now &&
            payload.now?.playIndex === latest.now.playIndex
        )
            payload = { ...payload, contenders: (latest as any).contenders } as Payload
        if (payload) latest = { ...payload, receivedAt: now() }
        listeners.forEach((_listening, listener) => {
            try {
                listener(payload ? latest : null)
            } catch {}
        })
        schedule(!payload)
        return payload ? latest : null
    }

    function read(options: ReadOptions = {}): Promise<any> {
        const wantsListening = options.listening ?? listening()
        const needsContenders = options.contenders ?? wantsContenders()
        if (inFlight) {
            if (
                !options.fresh &&
                (!wantsListening || inFlightListening) &&
                (!needsContenders || inFlightContenders)
            )
                return inFlight
            // Coalesce signals received during an old HTTP snapshot into one
            // subsequent read, including promotion from passive to listening.
            const queuedGeneration = generation
            return (queuedRead ||= inFlight.then(() => {
                if (queuedGeneration !== generation) return null
                queuedRead = null
                return read({
                    listening: wantsListening || listening(),
                    contenders: needsContenders || wantsContenders(),
                })
            }))
        }
        if (!isVisible() && !wantsListening) {
            schedule()
            return Promise.resolve(latest)
        }
        const requestGeneration = generation
        inFlightListening = wantsListening
        inFlightContenders = needsContenders
        inFlight = deps
            .fetchNow({ listening: wantsListening, fresh: true, contenders: needsContenders })
            .catch(() => null)
            .then(payload => {
                if (requestGeneration !== generation) return payload
                inFlight = null
                return publish(payload)
            })
        return inFlight
    }

    return {
        subscribe(listener: Listener, options: SubscribeOptions = {}) {
            const wasListening = listening()
            const hadContenders = listeners.size > 0 && wantsContenders()
            listeners.set(listener, {
                listening: Boolean(options.listening),
                contenders: options.contenders !== false,
            })
            if (latest && listeners.size === 1 && !stillOnAir(latest)) latest = null
            if (latest) listener(latest)
            if (listeners.size === 1) {
                stopVisibility = onVisible(() => {
                    void read({ fresh: true })
                })
                stopUpdates = deps.subscribeUpdates?.(() => {
                    void read({ fresh: true })
                })
                void read()
            } else if ((!wasListening && listening()) || (!hadContenders && wantsContenders())) {
                void read({ fresh: true })
            }
            return () => {
                listeners.delete(listener)
                if (listeners.size) return
                generation++
                inFlight = null
                queuedRead = null
                if (timer != null) clearTimer(timer)
                timer = null
                // Kept while its song is on air: coming back shows it at once,
                // and the first read replaces it.
                if (!stillOnAir(latest)) latest = null
                stopVisibility?.()
                stopUpdates?.()
            }
        },
        refresh: read,
        publish,
        latest: () => latest,
    }
}

export const radioNowFeed = createRadioNowFeed({
    fetchNow: fetchRadioNow,
    subscribeUpdates: refresh => {
        let stopped = false
        let unsubscribe: (() => void) | undefined
        void import('state/radioTicks').then(({ subscribeRadioTicks }) => {
            if (!stopped) unsubscribe = subscribeRadioTicks(refresh)
        })
        return () => {
            stopped = true
            unsubscribe?.()
        }
    },
})
export const subscribeRadioNow = (listener: Listener, options: SubscribeOptions = {}) =>
    radioNowFeed.subscribe(listener, options)
